import type { CardTextProps } from '@Components/BigBrowse/GridCardMesh/Components/Texts/CardText';
import type {
  PlaneSize,
  Position2D,
} from '@Components/BigBrowse/Model/ModelTypes';
import { useEffect, useState } from 'react';
// @ts-expect-error
import { requestFlexLayout } from 'troika-flex-layout';

export type GetTextBlockSizeOptions = {
  // This fixes a bug where when the calculated text size is one line and near the max-width,
  // the calculation returns the height as one line, while the Troika "Text" element renders it as two lines.
  // Thus, we bump the font size to cause the calculation to return two lines, in those edge cases.
  applyScaleFix?: boolean;
};

// The scaleFix we have seen working when fontSize is 44 and bumpSize is 45.
// so we assume a corresponding scale factor will work for the other font sizes.
const SCALE_BUMP = 45 / 44;

export const getTextBlockSize = (
  textProps: CardTextProps,
  options: GetTextBlockSizeOptions = {}
): Promise<{ width: number; height: number }> =>
  new Promise((resolve) => {
    const effectiveFontSize = options.applyScaleFix
      ? (textProps.fontSize ?? 0) * SCALE_BUMP
      : textProps.fontSize;

    requestFlexLayout(
      {
        id: 'text',
        ...textProps,
        fontSize: effectiveFontSize,
      },
      (result: { text: { width: number; height: number } }) => {
        resolve({
          width: result.text.width,
          height: result.text.height,
        });
      }
    );
  });

export const useGetTextBlockSize = (
  textProps: CardTextProps,
  options: GetTextBlockSizeOptions = {}
) => {
  const [textSize, setTextSize] = useState<PlaneSize | undefined>(undefined);

  useEffect(() => {
    const calculate = async () => {
      const size = await getTextBlockSize(textProps, options);
      setTextSize(size);
    };

    calculate().then();
  }, [textProps]);

  return textSize;
};

export const getRectanglePositionInsideRectangle =
  (wrapper: PlaneSize) =>
  (rectangle: PlaneSize) =>
  (edges: { top: number; left: number }): Position2D => ({
    x: Math.round((-wrapper.width + rectangle.width) / 2 + edges.left),
    y: Math.round((wrapper.height - rectangle.height) / 2 - edges.top),
  });

/** Transforms a point from canvas coordinates to web box coordinates. */
export function transformPoint(
  canvas: PlaneSize,
  box: PlaneSize,
  point: Position2D = { x: 0, y: 0 }
): Position2D {
  // Translate the point to have the origin in the top-left corner.
  const translatedPoint: Position2D = {
    x: point.x + canvas.width / 2,
    y: -point.y + canvas.height / 2,
  };

  // Scale the point to fit into the box.
  return {
    x: (translatedPoint.x * box.width) / canvas.width,
    y: (translatedPoint.y * box.height) / canvas.height,
  };
}
